import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signIn, initAuthTables } from "@/lib/auth";
import { setSessionToken, getSessionToken } from "@/lib/sessionStorage";
import { SocialLoginButtons, loginWithGoogle, loginWithFacebook, loginWithApple } from "@/components/SocialLoginButtons";
import { AgeVerification, type AgeRestrictions } from "@/components/AgeVerification";
import { supabase } from '@/lib/supabase';
import { db } from '@/database/Database';
import { useAuth } from '@/contexts/AuthContext';

const OrganizerSignIn: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(true); // NEW: Track OAuth processing

  // Handle OAuth on mount - simple and clean
  useEffect(() => {
    initAuthTables();
    processOAuthIfPresent();
  }, []);

  async function processOAuthIfPresent() {
    console.log('🔍 Checking for OAuth session...');
    setIsProcessingOAuth(true); // Start processing
    
    // Get session from Supabase
    const { data: { session }, error } = await supabase.auth.getSession();
    
    console.log('📊 OAuth Check Results:', {
      hasSession: !!session,
      hasUser: !!session?.user,
      userEmail: session?.user?.email,
      error: error?.message,
      alreadySignedIn: !!localStorage.getItem('whipt_session_token')
    });
    
    // No session? Stop here
    if (!session?.user) {
      console.log('❌ No OAuth session found');
      setIsProcessingOAuth(false); // Done processing
      return;
    }
    
    const existingToken = localStorage.getItem('whipt_session_token');
    if (existingToken) {
      console.warn('⚠️ Existing local session token found. Replacing it with fresh OAuth session token.');
    }
    
    const user = session.user;
    console.log('🔐 OAuth session detected! Processing sign-in for:', user.email);
    
    try {
      const userId = user.id;
      const userName = user.user_metadata.full_name || user.user_metadata.name || 'User';
      
      // Create/update user in database
      console.log('💾 Creating/updating user in database...');
      try {
        // OAuth users don't have passwords, so use a placeholder
        const oauthPasswordPlaceholder = 'OAUTH_USER_NO_PASSWORD';
        
        await db.query(`
          INSERT INTO users (id, email, name, password_hash, created_at, last_active_at, show_online_status, role)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?)
          ON CONFLICT(id) DO UPDATE SET
            last_active_at = ?,
            name = ?
        `, [
          userId,
          user.email,
          userName,
          oauthPasswordPlaceholder,
          new Date().toISOString(),
          new Date().toISOString(),
          true,
          'user',
          new Date().toISOString(),
          userName
        ]);
        
        // Create session record in database
        const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(); // 30 days
        await db.query(`
          INSERT INTO sessions (token, user_id, expires_at, created_at)
          VALUES (?, ?, ?, ?)
          ON CONFLICT(token) DO UPDATE SET
            expires_at = ?,
            user_id = ?
        `, [
          session.access_token,
          userId,
          expiresAt,
          new Date().toISOString(),
          expiresAt,
          userId
        ]);
        
        console.log('✅ User and session created/updated in database');
      } catch (dbError) {
        console.error('⚠️ Database error (continuing anyway):', dbError);
        // Continue even if DB fails - use localStorage as fallback
      }

      // Store session in BOTH formats (for localStorage fallback compatibility)
      localStorage.setItem('whipt_session_token', session.access_token);
      const storedUser = {
        id: userId,
        email: user.email,
        name: userName,
        created_at: new Date().toISOString(),
        last_active_at: new Date().toISOString(),
        show_online_status: true,
        role: 'user'
      } as const;
      localStorage.setItem('whipt_user', JSON.stringify(storedUser));
      
      // ALSO store in dev storage format for verifySession() localStorage fallback
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      
      // Store user in dev users array
      const devUsers = JSON.parse(localStorage.getItem('whipt_dev_users') || '[]');
      const existingUserIndex = devUsers.findIndex((u: any) => u.id === userId);
      if (existingUserIndex >= 0) {
        devUsers[existingUserIndex] = {
          ...storedUser,
          password_hash: 'OAUTH_USER_NO_PASSWORD'
        };
      } else {
        devUsers.push({
          ...storedUser,
          password_hash: 'OAUTH_USER_NO_PASSWORD'
        });
      }
      localStorage.setItem('whipt_dev_users', JSON.stringify(devUsers));
      
      // Store session in dev sessions array
      const devSessions = JSON.parse(localStorage.getItem('whipt_dev_sessions') || '[]');
      const existingSessionIndex = devSessions.findIndex((s: any) => s.token === session.access_token);
      if (existingSessionIndex >= 0) {
        devSessions[existingSessionIndex] = {
          token: session.access_token,
          user_id: userId,
          expires_at: expiresAt,
          created_at: new Date().toISOString()
        };
      } else {
        devSessions.push({
          token: session.access_token,
          user_id: userId,
          expires_at: expiresAt,
          created_at: new Date().toISOString()
        });
      }
      localStorage.setItem('whipt_dev_sessions', JSON.stringify(devSessions));
      console.log('✅ Stored session in both standard AND dev storage formats');

      // Clear onboarding flag - OAuth users don't need onboarding flow
      localStorage.removeItem('whipt_needs_onboarding');
      
      // Set age verification flag to skip age verification modal
      // OAuth users are already verified through their Google account
      localStorage.setItem('whipt_age_verification', JSON.stringify({
        birthYear: 1990, // Default - they're verified adults
        acceptedTerms: true,
        verifiedAt: new Date().toISOString()
      }));
      
      console.log('✅ Cleared onboarding flag and set age verification for OAuth user');

      console.log('🔄 Refreshing auth context...');
      await refreshUser();
      console.log('✅ refreshUser() completed');
      
      // CRITICAL: Wait for React to update the auth context
      // React state updates are async, need to give it time to propagate
      await new Promise(resolve => setTimeout(resolve, 200));
      console.log('✅ Waited for auth context update');
      
      // CRITICAL: Set this BEFORE navigate to stop spinner
      setIsProcessingOAuth(false);
      
      console.log('✅ Session stored! Redirecting to /organizer');
      console.log('📊 Final localStorage state:');
      console.log('  - whipt_session_token:', localStorage.getItem('whipt_session_token') ? 'SET' : 'NOT SET');
      console.log('  - whipt_user:', localStorage.getItem('whipt_user') ? 'SET' : 'NOT SET');
      console.log('  - whipt_age_verification:', localStorage.getItem('whipt_age_verification') ? 'SET' : 'NOT SET');
      navigate('/organizer', { replace: true });
      
    } catch (err) {
      console.error('❌ OAuth processing error:', err);
      setError('Sign-in failed. Please try again.');
      setIsProcessingOAuth(false); // Done processing even on error
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      console.log('🔐 Attempting sign-in for:', email);
      const result = await signIn(email, password);
      console.log('🔐 Sign-in result:', result);
      
      if (result.success && result.user && result.session_token) {
        console.log('✅ Sign-in successful, storing session...');
        // Store session (now using persistent storage utility)
        setSessionToken(result.session_token);
        localStorage.setItem('whipt_user', JSON.stringify(result.user));
        console.log('✅ Session stored to persistent storage (localStorage + cookie)');

        console.log('🔄 Refreshing auth context in background...');
        // Don't await - let it happen in background to avoid spinner
        refreshUser().catch(err => console.warn('Background refreshUser failed:', err));
        
        // Check if user has age verification in localStorage first
        let ageVerification = localStorage.getItem('whipt_age_verification');
        console.log('🎂 Age verification from localStorage:', ageVerification ? 'Found' : 'Not found');
        
        // If not in localStorage, check database (but don't wait for it with 2sec timeout)
        if (!ageVerification) {
          console.log('🔍 Skipping database age verification check (using localStorage-only mode)');
          // Don't query DB - just proceed without age verification
        }
        
        if (!ageVerification) {
          // No age verification - for existing users, just let them in
          console.log('📝 No age verification found - allowing sign-in for existing user');
          const organizerMode = localStorage.getItem('whipt_organizer_mode');
          
          // Always send to student organizer for now
          navigate("/organizer");
        } else {
          // Has age verification - check if account is active
          const restrictions = JSON.parse(ageVerification);
          console.log('🔒 Account restrictions:', restrictions);
          
          if (restrictions.accountActive === false) {
            // Account pending parental consent
            console.log('❌ Account not active - pending parental consent');
            setError("Your account is pending parental consent. Please check your parent's email.");
            localStorage.removeItem('whipt_session_token');
            localStorage.removeItem('whipt_user');
          } else {
            // Account active - check if they've already chosen an organizer
            console.log('✅ Account active - checking organizer preference...');
            const organizerMode = localStorage.getItem('whipt_organizer_mode');
            console.log('📋 Organizer mode:', organizerMode);
            
            console.log('➡️ Redirecting to student organizer...');
            navigate("/organizer");
          }
        }
      } else {
        console.log('❌ Sign-in failed:', result.error);
        setError(result.error || "Failed to sign in");
      }
    } catch (err) {
      console.error('❌ Sign-in error:', err);
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAgeVerified = (restrictions: AgeRestrictions) => {
    // Age verification complete
    if (restrictions.accountActive) {
      // Reload to update AuthContext with new session
      window.location.href = "/organizer";
    } else {
      // Account pending parental consent
      setShowAgeVerification(false);
      setError("Your account is pending parental consent. You'll receive an email when it's activated.");
      localStorage.removeItem('whipt_session_token');
      localStorage.removeItem('whipt_user');
    }
  };

  // Show loading spinner while processing OAuth
  if (isProcessingOAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] text-white flex flex-col items-center justify-center px-4">
        <div className="text-center space-y-6">
          <div className="w-16 h-16 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-amber-200">Signing you in...</h2>
            <p className="text-slate-400 text-sm">Please wait a moment</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] text-white flex items-center justify-center px-4">
      {showAgeVerification && (
        <AgeVerification 
          onVerified={handleAgeVerified}
          onClose={() => {
            setShowAgeVerification(false);
            navigate("/organizer");
          }}
        />
      )}
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-8 space-y-4">
          <div className="inline-block">
            <div className="border-4 border-black rounded-lg p-4 bg-white/5 backdrop-blur-sm">
              <img
                src="/pronoia-wellness-hero2.webp"
                alt="Pronoia Wellness"
                className="max-w-xs w-auto object-contain mx-auto"
              />
            </div>
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold mb-1">
              <span className="bg-gradient-to-r from-amber-200 via-orange-300 to-amber-200 bg-clip-text text-transparent">
                Where'd I Put That?
              </span>
            </h1>
            <p className="text-xs uppercase tracking-[0.4em] text-amber-200/60">
              Scholarly Student Organizer
            </p>
          </div>
        </div>

        {/* Sign In Panel */}
        <div className="rounded-3xl border border-white/15 bg-black/40 backdrop-blur-xl p-8 space-y-6 shadow-[0_25px_120px_rgba(0,0,0,0.7)]">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-semibold">Sign In</h2>
            <p className="text-sm text-slate-300">Welcome back! Enter your credentials to continue.</p>
          </div>

          {error && (
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block text-sm font-semibold tracking-wide text-slate-100">
              Email
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-2 w-full rounded-xl border border-white/20 bg-black/30 px-4 py-3 text-sm focus:border-amber-300 focus:ring-2 focus:ring-amber-300/20 outline-none transition-all"
                placeholder="you@university.edu"
                required
                disabled={isLoading}
              />
            </label>

            <label className="block text-sm font-semibold tracking-wide text-slate-100">
              Password
              <div className="relative mt-2">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full rounded-xl border border-white/20 bg-black/30 px-4 py-3 pr-12 text-sm focus:border-amber-300 focus:ring-2 focus:ring-amber-300/20 outline-none transition-all"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 text-sm"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </label>

            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-bold py-3 text-sm uppercase tracking-[0.2em] hover:shadow-[0_8px_32px_rgba(251,146,60,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </button>
          </form>

          {/* Social Login Buttons */}
          <SocialLoginButtons 
            onGoogleLogin={async () => {
              try {
                await loginWithGoogle();
              } catch (err) {
                setError(typeof err === 'string' ? err : 'Social login is not yet available. Please use email and password.');
              }
            }}
            onFacebookLogin={async () => {
              try {
                await loginWithFacebook();
              } catch (err) {
                setError(typeof err === 'string' ? err : 'Social login is not yet available. Please use email and password.');
              }
            }}
            onAppleLogin={async () => {
              try {
                await loginWithApple();
              } catch (err) {
                setError(typeof err === 'string' ? err : 'Social login is not yet available. Please use email and password.');
              }
            }}
          />

          <div className="text-center pt-4 space-y-3">
            <p className="text-sm text-slate-400">
              Don't have an account?{" "}
              <Link to="/organizer/sign-up" className="text-amber-300 hover:text-amber-200 font-semibold">
                Sign Up
              </Link>
            </p>
            <Link 
              to="/" 
              className="block text-xs uppercase tracking-[0.3em] text-white/50 hover:text-white/80 transition-colors"
            >
              ← Back to Landing
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizerSignIn;
